import { FormEvent, useState } from 'react'
import { ArrowLeft, Loader2, Mail, MailCheck } from 'lucide-react'
import { Link } from 'react-router-dom'

import { authService } from '../services/authService'
import schoolCrest from '../assets/logos/crest.png'

const SCHOOL_NAME = 'The Promise Standard School'

export default function ForgotPassword() {
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [error, setError] = useState('')
  const [sending, setSending] = useState(false)

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError('')
    setSending(true)
    try {
      await authService.forgotPassword(email.trim())
      setSent(true)
    } catch {
      setError('The request could not be sent. Check your connection and try again.')
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#f5f2ec] px-5 py-10 text-[#12233f] antialiased sm:px-8">
      <div className="w-full max-w-md">
        <img
          src={schoolCrest}
          alt={`${SCHOOL_NAME} crest`}
          className="relative z-10 mx-auto -mb-[4.5rem] h-32 w-32 object-contain drop-shadow-sm sm:h-36 sm:w-36"
        />

        <div className="rounded-2xl border border-slate-200/70 bg-white px-6 pb-8 pt-[5.5rem] shadow-xl shadow-slate-900/[0.06] sm:px-9">
          {sent ? (
            <div className="text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                <MailCheck className="h-6 w-6" />
              </div>
              <h1 className="mt-4 text-xl font-bold tracking-tight text-[#12233f] sm:text-2xl">
                Check your email
              </h1>
              {/* The same reply whether or not the address is known, so it reveals no accounts. */}
              <p className="mt-3 text-sm leading-6 text-slate-600">
                If an account uses <span className="font-semibold text-[#12233f]">{email.trim()}</span>, a
                link to set a new password is on its way. It works for one hour. An email shared by several
                accounts gets a link for each.
              </p>
              <Link
                to="/login"
                className="mt-7 flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#8c1d18] to-[#6f1613] px-5 py-3.5 text-sm font-bold text-[#f0d9a8] shadow-lg shadow-[#8c1d18]/20 transition-all duration-200 hover:from-[#7a1915] hover:to-[#5e120f] hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-[#8c1d18]/20 active:scale-[0.99]"
              >
                Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <div className="text-center">
                <h1 className="text-xl font-bold tracking-tight text-[#12233f] sm:text-2xl">
                  Reset your password
                </h1>
                <p className="mt-2 text-sm leading-6 text-slate-500">
                  Enter the email address on your account and we will send you a link to set a new
                  password.
                </p>
              </div>

              <form onSubmit={submit} className="mt-6 space-y-4">
                {error && (
                  <div
                    role="alert"
                    className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-800"
                  >
                    {error}
                  </div>
                )}

                <div>
                  <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-slate-700">
                    Email address
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-slate-400">
                      <Mail className="h-4 w-4" />
                    </div>
                    <input
                      id="email"
                      required
                      type="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      autoComplete="email"
                      placeholder="you@example.com"
                      className="w-full rounded-lg border border-slate-300 bg-white py-3 pl-10 pr-4 text-sm text-[#12233f] shadow-sm outline-none transition placeholder:text-slate-400 focus:border-[#8c1d18] focus:ring-4 focus:ring-[#8c1d18]/10"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={sending}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#8c1d18] to-[#6f1613] px-5 py-3.5 text-sm font-bold text-[#f0d9a8] shadow-lg shadow-[#8c1d18]/20 transition-all duration-200 hover:from-[#7a1915] hover:to-[#5e120f] hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-[#8c1d18]/20 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {sending ? (
                    <>
                      Sending...
                      <Loader2 className="h-4 w-4 animate-spin" />
                    </>
                  ) : (
                    'Send reset link'
                  )}
                </button>
              </form>

              <Link
                to="/login"
                className="mt-6 flex items-center justify-center gap-1.5 text-sm font-medium text-[#8c1d18] hover:text-[#6f1613]"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to sign in
              </Link>
            </>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-slate-400">
          {SCHOOL_NAME} &middot; Campus Portal Administration
        </p>
      </div>
    </div>
  )
}
